import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, StatusBadge, Table, SortableTh, ClampText, EmptyValue, EXPAND_COL_WIDTH, IdLink, RowExpandToggle, TableScroll, BulkActionBar, Spinner } from '@/components/ui';
import { Effort } from '../../../types/Effort';
import { RoutineTask } from '../../../types/RoutineTask';
import TaskEffortsTable from '../../../components/TaskEffortsTable';
import '../styles/adaptive.css';
import RoutineTaskActions from './RoutineTaskActions';
import { useResizableColumns } from '../../../hooks/useResizableColumns';
import { ResizeHandle } from '../../../components/ResizeHandle';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatDate as formatPacificDate } from '@/utils/displayDateTime';

const ROUTINE_EFFORTS_COLUMNS = {
  type: true,
  date: true,
  employeeName: true,
  description: true,
  accomplishment: true,
  quantity: true,
  rate: true,
  fuelSurcharge: true,
  totalCost: true,
  comments: true
} as const;

/** Checkbox column width; the pinned Task ID column sits right after it (W-iP11). */
const CHECK_COL_WIDTH = '2.75rem';
/** V2-072 / ACT-04: Task ID is pinned after the checkbox AND the expand column. */
const TASK_ID_PIN_LEFT = `calc(${CHECK_COL_WIDTH} + ${EXPAND_COL_WIDTH})`;
/** Expander (32px) + kebab (32px) + gap + cell padding: the Actions column fits its controls (W-M8). */
const ACTIONS_COL_WIDTH_ROUTINE = '6.5rem';

/**
 * Routine task names are usually composites like "Mark Wiltshire - Hydrant inspection". In a
 * narrow column every row used to read "Mark Wiltshire ..." (W-iP11), so the part after the first
 * " - " (the meaningful detail) goes on line 1 and the leading part (usually the employee) goes
 * muted on line 2. The full name is always in the tooltip.
 */
export function splitCompositeName(name: string): { detail: string; lead: string | null } {
  const idx = name.indexOf(' - ');
  if (idx <= 0) return { detail: name, lead: null };
  const lead = name.slice(0, idx).trim();
  const detail = name.slice(idx + 3).trim();
  if (!detail || !lead) return { detail: name, lead: null };
  return { detail, lead };
}

export const RoutineTaskName: React.FC<{ name: string }> = ({ name }) => {
  const { detail, lead } = splitCompositeName(name);
  if (!lead) return <ClampText title={name}>{name}</ClampText>;
  return (
    <span className="tw-block tw-min-w-0" title={name} data-testid="routine-task-name">
      <ClampText lines={2} title={name}>{detail}</ClampText>
      <ClampText lines={1} title={name} className="tw-text-sm tw-text-content-muted">{lead}</ClampText>
    </span>
  );
};

// Add new interface for modal props
interface EffortModalProps {
  show: boolean;
  onHide: () => void;
  task: RoutineTask;
  canToggleVehicleFuel: boolean;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
}

interface RoutineWorkTableProps {
  tasks: RoutineTask[];
  onTaskAction: (action: string, task: RoutineTask) => void;
  canCloneTask?: boolean;
  onTaskSelect: (taskIds: number[]) => void;
  onBulkAction: (action: string, tasks: RoutineTask[]) => void;
  selectedTasks: number[];
  onTaskClick?: (task: RoutineTask) => void;
  showBulkActions?: boolean;
  showAsset?: boolean;
  showQuantity?: boolean;
  canReopenTask?: boolean;
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
  /** Shows a spinner over the table while the page refetches; the bulk bar and the table box stay put. */
  loading?: boolean;
  /** Active column. Null means the list is in its default order (newest task id). */
  sortField?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  /**
   * True when the parent already asked the server to order the full filtered set.
   * Those rows must be shown as returned. Re-sorting them here would only reorder the page.
   */
  serverSorted?: boolean;
}

// Update EffortModal component
const EffortModal: React.FC<EffortModalProps> = ({
  show,
  onHide,
  task,
  canToggleVehicleFuel,
  onVehicleFuelEffortPatched
}) => {
  return (
    // autoHeight: this modal is the tablet path for viewing efforts (screenSize === 'tablet'
    // opens it instead of expanding the row in place). Without it, `lg` gets the standard fixed
    // 85vh box - grouping efforts by type, the last group ("Vehicle" sorts after Labour/Material/
    // Other alphabetically) landed right at that boundary, so it read as truncated instead of
    // needing a scroll. Content-sizing removes the fixed ceiling. Matches the equivalent modal on
    // the Quantified Tasks list, which already does this.
    <Modal show={show} onHide={onHide} size="lg" centered autoHeight>
      <Modal.Header closeButton onHide={onHide}>
        <Modal.Title>Effort Details - Task {task.taskNumber || task.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <TaskEffortsTable
          efforts={task.efforts || []}
          columns={{ ...ROUTINE_EFFORTS_COLUMNS }}
          canToggleVehicleFuel={canToggleVehicleFuel}
          onVehicleFuelEffortPatched={onVehicleFuelEffortPatched}
        />
      </Modal.Body>
    </Modal>
  );
};

const RoutineWorkTable: React.FC<RoutineWorkTableProps> = ({ 
  tasks, 
  onTaskAction,
  onTaskSelect,
  onBulkAction,
  selectedTasks,
  onTaskClick,
  canReopenTask = true,
  canCloneTask = false,
  showBulkActions = true,
  showAsset = false,
  showQuantity = false,
  loading = false,
  onVehicleFuelEffortPatched: onVehicleFuelEffortPatchedFromParent,
  sortField = null,
  sortDirection = 'asc',
  onSort = () => {},
  serverSorted = false
}) => {
  const { hasPermission } = usePermissions();
  const canToggleVehicleFuel = hasPermission('quantified:task_edit');
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [isTableLoaded, setIsTableLoaded] = useState(false);
  const [selectedTaskForModal, setSelectedTaskForModal] = useState<RoutineTask | null>(null);

  const routineTaskMatchesEffort = (t: RoutineTask, effort: Effort) =>
    effort.taskId != null && (t.id === effort.taskId || t.taskId === effort.taskId);

  const handleVehicleFuelEffortPatched = useCallback(
    (effort: Effort) => {
      setSelectedTaskForModal((prev) => {
        if (!prev || !routineTaskMatchesEffort(prev, effort)) return prev;
        return {
          ...prev,
          efforts: (prev.efforts || []).map((e) => (e.id === effort.id ? { ...e, ...effort } : e))
        };
      });
      onVehicleFuelEffortPatchedFromParent?.(effort);
    },
    [onVehicleFuelEffortPatchedFromParent]
  );

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // Actions is not resizable any more: it is pinned right and sized to its controls (W-M8).
  const RESIZABLE_COLUMN_IDS = ['taskNumber', 'date', 'name', 'description', 'asset', 'quantity', 'activity', 'status'];
  const defaultWidths: Record<string, string> = {
    taskNumber: '90px',
    date: '120px',
    // Long-text columns get room for a readable 2-line clamp (W-iP11).
    name: '300px',
    description: '300px',
    asset: '240px',
    quantity: '120px',
    activity: '240px',
    // Wide enough for the longest badge ("Active - Marked as Complete").
    status: '150px'
  };
  const { getColumnWidth, getColumnWidthPx, startResize } = useResizableColumns(
    RESIZABLE_COLUMN_IDS,
    { defaultWidths }
  );

  // Simplified device detection
  const checkScreenSize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Update orientation
    setOrientation(width > height ? 'landscape' : 'portrait');
    
    // Determine screen size
    if (width <= 768) {
      setScreenSize('mobile');
    } else if (width <= 1200) {
      setScreenSize('tablet');
    } else {
      setScreenSize('desktop');
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkScreenSize();
    
    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, [checkScreenSize]);

  useEffect(() => {
    // Set table as loaded after a short delay to trigger animations
    const timer = setTimeout(() => {
      setIsTableLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // The synced "sticky scrollbar" strip that sat ABOVE the header row is gone (W-iP11): the
  // table's own scroll box (TableScroll) scrolls on both axes, so its horizontal scrollbar is at the
  // bottom of the table.

  // Debug tasks prop when it changes
  useEffect(() => {
    if (tasks.length > 0) {
      console.log('RoutineWorkTable received tasks:', tasks.length);
      console.log('First task structure sample:', {
        id: tasks[0].id,
        taskId: tasks[0].taskId,
        taskNumber: tasks[0].taskNumber,
        has_activities: Array.isArray(tasks[0].activities),
        activities_length: Array.isArray(tasks[0].activities) ? tasks[0].activities.length : 0,
        first_activity: Array.isArray(tasks[0].activities) && tasks[0].activities.length > 0 
          ? tasks[0].activities[0] : null,
        has_taskLocations: Array.isArray(tasks[0].taskLocations),
        locations_length: Array.isArray(tasks[0].taskLocations) ? tasks[0].taskLocations.length : 0,
        first_location: Array.isArray(tasks[0].taskLocations) && tasks[0].taskLocations.length > 0
          ? tasks[0].taskLocations[0] : null,
        has_efforts: Array.isArray(tasks[0].efforts)
      });
    }
  }, [tasks]);

  const formatDate = (date: Date) => formatPacificDate(date);


  // V2-073: the shared formatter handles date-only values without a time-zone shift.
  const formatEffortDate = (dateStr: string | undefined) => (dateStr ? formatPacificDate(dateStr) || 'Unknown' : 'Unknown');


  // Helper function to get the earliest work completion date for a task
  const getTaskWorkDate = (task: RoutineTask): string => {
    // Priority order: startDate > earliest effort date > completion date > identified date > created date
    if (task.startDate) return task.startDate;
    
    if (task.efforts && task.efforts.length > 0) {
      // Get the earliest effort date (first work completed)
      const effortDates = task.efforts
        .map(effort => effort.date)
        .filter(date => date)
        .sort((a, b) => new Date(a!).getTime() - new Date(b!).getTime());
      
      if (effortDates.length > 0) {
        return effortDates[0]!;
      }
    }
    
    // Fallback to task dates
    if (task.completionDate) return task.completionDate;
    if (task.identifiedDate) return task.identifiedDate;
    if (task.createdAt) return task.createdAt;
    
    return '';
  };

  const handleSelectTask = (taskId: number) => {
    const newSelected = new Set(selectedTasks || []);
    if (selectedTasks && selectedTasks.includes(taskId)) {
      newSelected.delete(taskId);
    } else {
      newSelected.add(taskId);
    }
    onTaskSelect?.(Array.from(newSelected));
  };

  const handleSelectAll = () => {
    if (selectedTasks && selectedTasks.length === tasks.length) {
      onTaskSelect?.([]);
    } else {
      onTaskSelect?.(tasks.map(task => task.id));
    }
  };

  const handleBulkAction = (action: string) => {
    const selectedTaskObjects = tasks.filter(task => selectedTasks.includes(task.id));
    onBulkAction(action, selectedTaskObjects);
    // Dropdown (Headless UI Menu) closes itself on item activation - no open-state to reset here.
  };

  // Previously getStatusBadgeClass/getStatusDisplay: a local status->colour map that, unlike the
  // shared one, coloured ACTIVE_MARKED_AS_COMPLETE 'success' (green) and rendered it as a plain
  // "Active" badge plus a separate green check icon - an active task reading as already finished,
  // and differently from the identical status on the Quantified Tasks list. StatusBadge is the
  // single source of truth for status colour/wording (see utils/statusUtils), so this task and
  // that one now render identically wherever they're shown.
  const toggleRowExpansion = (taskId: number, task: RoutineTask) => {
    if (screenSize === 'tablet') {
      setSelectedTaskForModal(task);
    } else {
      setExpandedRows(prev => 
        prev.includes(taskId) 
          ? prev.filter(id => id !== taskId)
          : [...prev, taskId]
      );
    }
  };

  const calculateEffortTotal = (efforts: any[]) => {
    return efforts.reduce((sum, effort) => sum + (effort.quantity * effort.rate), 0);
  };

  // getSortIcon was replaced by SortableTh, which renders the same icon set itself from
  // sortField/sortDirection (passed through as activeKey/direction) - no separate helper needed.
  const renderEffortDetails = (task: RoutineTask) => {
    // Calculate dynamic colspan based on visible columns
    let colSpanCount = 9; // Base columns: checkbox, expand, task#, date, name, description, activity, status, actions
    if (showAsset) colSpanCount += 1;
    if (showQuantity) colSpanCount += 1;
    
    return (
      <tr>
        <td colSpan={colSpanCount}>
          <div className="tw-p-3 tw-bg-surface-alt tw-border tw-border-line tw-rounded">
            <TaskEffortsTable 
              efforts={task.efforts || []}
              columns={{ ...ROUTINE_EFFORTS_COLUMNS }}
              canToggleVehicleFuel={canToggleVehicleFuel}
              onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
            />
          </div>
        </td>
      </tr>
    );
  };

  // Asset and activity are labels from related rows, so they can only be ordered within the
  // page the server already returned. Every other column is ordered for the full filtered list
  // before pagination (serverSorted); re-sorting those rows here would undo that order.
  const displayTasks =
    !serverSorted && (sortField === 'asset' || sortField === 'activity')
      ? [...tasks].sort((a, b) => {
          const aValue =
            sortField === 'asset'
              ? a.taskLocations?.[0]?.asset?.name || ''
              : a.activities?.[0]?.activityName || '';
          const bValue =
            sortField === 'asset'
              ? b.taskLocations?.[0]?.asset?.name || ''
              : b.activities?.[0]?.activityName || '';
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        })
      : tasks;

  // Use displayTasks directly since pagination is handled by the parent component

  const bulkCount = (selectedTasks || []).filter((id) => tasks.some((t) => t.id === id)).length;

  return (
    <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col">
      {/* Selection strip (W-L7). It used to be inserted INSIDE the table box, with no padding, only
          once a row was selected - so ticking the first box pushed every row down. BulkActionBar
          is always rendered at one fixed height, outside the table's scroll box. */}
      {showBulkActions && (
        <BulkActionBar
          count={bulkCount}
          noun="task"
          onClearSelection={() => onTaskSelect?.([])}
          actions={[
            { key: 'cancel', label: 'Cancel Selected', onClick: () => handleBulkAction('cancel') },
            { key: 'set-active', label: 'Set to Active', onClick: () => handleBulkAction('set-active') },
            { key: 'duplicate', label: 'Duplicate Selected', onClick: () => handleBulkAction('duplicate') },
          ]}
        />
      )}

      {/* Mobile (< md): stacked cards — the wide routine table doesn't fit a phone.
          Full table returns at md+. */}
      <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
        {displayTasks.length === 0 ? (
          <div className="tw-py-6 tw-text-center tw-text-content-muted">No routine tasks found</div>
        ) : (
          displayTasks.map((task) => {
            const hasEfforts = !!task.efforts?.some((e) => e.quantity || e.rate);
            const activity =
              Array.isArray(task.activities) && task.activities.length > 0
                ? `#${task.activities[0].activityNumber ?? ''} ${task.activities[0].activityName ?? ''}`.trim()
                : '-';
            return (
              <div
                key={`m-${task.id}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedTasks && selectedTasks.includes(task.id)}
                      onChange={() => handleSelectTask(task.id)}
                      className="tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-line-input tw-text-primary"
                    />
                    <IdLink className="tw-truncate" onClick={(e) => { e.preventDefault(); onTaskClick?.(task); }}>
                      {task.taskNumber || `T-${task.id}`}
                    </IdLink>
                  </div>
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    <StatusBadge status={task.status} />
                    <RoutineTaskActions
                      task={task}
                      onAction={(action) => onTaskAction(action, task)}
                      canReopenTask={canReopenTask}
                      canCloneTask={canCloneTask}
                    />
                  </div>
                </div>
                {task.name && (
                  <div className="tw-mt-1 tw-font-medium tw-text-content">
                    <RoutineTaskName name={task.name} />
                  </div>
                )}
                {task.description && (
                  <div className="tw-mt-0.5 tw-break-words tw-text-sm tw-text-content-secondary">
                    <ClampText>{task.description}</ClampText>
                  </div>
                )}
                <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Date</div>
                    <div className="tw-text-content">{formatEffortDate(getTaskWorkDate(task))}</div>
                  </div>
                  <div className="tw-min-w-0">
                    <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Activity</div>
                    <ClampText lines={1} as="div" className="tw-text-content" title={activity}>{activity}</ClampText>
                  </div>
                  {showQuantity && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Quantity</div>
                      <div className="tw-tabular-nums tw-text-content">
                        {typeof task.completionAccomplishment === 'number'
                          ? task.completionAccomplishment.toFixed(2)
                          : '-'}
                      </div>
                    </div>
                  )}
                </div>
                {hasEfforts && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="tw-mt-3 tw-w-full"
                    onClick={() => setSelectedTaskForModal(task)}
                    title="View efforts"
                  >
                    <i className="bi bi-list-ul tw-mr-1" />
                    View efforts
                  </Button>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* The ONE scroll box for the table (both axes): the sticky header and the pinned checkbox /
          Task ID / Actions columns attach to it (W-M20, W-M8). Nothing between it and <table> may
          have overflow. Fills ListPage.Body. */}
      <TableScroll ref={tableWrapperRef} className="tw-hidden md:tw-block">
        {loading && (
          <div
            data-testid="routine-table-loading"
            className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-bg-surface/70"
          >
            <Spinner>Loading...</Spinner>
          </div>
        )}
        {/* Routed through the shared Table primitive (striped + hover, the app-standard header
            weight). Task number, start date, name, description, quantity, and status are ordered
            on the server for the full filtered list. Asset and activity still sort only the
            current page. Selection and expansion stay local, and resizable columns keep this
            off the shared DataTable. */}
        <Table striped hover stickyHeader style={{ tableLayout: 'fixed', minWidth: '100%', width: 'max-content' }}>
        <thead>
          <tr>
            <th className="wrap-touch-check wrap-pin-left" style={{ width: CHECK_COL_WIDTH }}>
              <input
                type="checkbox"
                className="tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary"
                checked={selectedTasks && selectedTasks.length === tasks.length && tasks.length > 0}
                onChange={handleSelectAll}
              />
            </th>
            {/* V2-072 / ACT-04: the expand column, after the checkbox, before Task ID. */}
            <th
              className="wrap-pin-left"
              style={{ width: EXPAND_COL_WIDTH, '--wrap-pin-left': CHECK_COL_WIDTH } as React.CSSProperties}
              aria-label="Expand"
            />
            <SortableTh
              className="wrap-pin-left wrap-pin-edge"
              style={{ width: getColumnWidth('taskNumber'), '--wrap-pin-left': TASK_ID_PIN_LEFT } as React.CSSProperties}
              label="Task ID"
              sortKey="taskNumber"
              activeKey={sortField}
              direction={sortDirection}
              onSort={onSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="taskNumber" currentWidthPx={getColumnWidthPx('taskNumber')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('date') }}
              label="Start Date"
              sortKey="startDate"
              activeKey={sortField}
              direction={sortDirection}
              onSort={onSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="date" currentWidthPx={getColumnWidthPx('date')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('name') }}
              label="Name"
              sortKey="name"
              activeKey={sortField}
              direction={sortDirection}
              onSort={onSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="name" currentWidthPx={getColumnWidthPx('name')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('description') }}
              label="Description"
              sortKey="description"
              activeKey={sortField}
              direction={sortDirection}
              onSort={onSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="description" currentWidthPx={getColumnWidthPx('description')} onResizeStart={startResize} />
              )}
            </SortableTh>
            {showAsset && (
              <SortableTh
                style={{ width: getColumnWidth('asset') }}
                label="Asset"
                sortKey="asset"
                activeKey={sortField}
                direction={sortDirection}
                onSort={onSort}
              >
                {screenSize === 'desktop' && (
                  <ResizeHandle columnId="asset" currentWidthPx={getColumnWidthPx('asset')} onResizeStart={startResize} />
                )}
              </SortableTh>
            )}
            {showQuantity && (
              <SortableTh
                style={{ width: getColumnWidth('quantity') }}
                label="Quantity"
                sortKey="quantity"
                activeKey={sortField}
                direction={sortDirection}
                onSort={onSort}
              >
                {screenSize === 'desktop' && (
                  <ResizeHandle columnId="quantity" currentWidthPx={getColumnWidthPx('quantity')} onResizeStart={startResize} />
                )}
              </SortableTh>
            )}
            <SortableTh
              style={{ width: getColumnWidth('activity') }}
              label="Activity"
              sortKey="activity"
              activeKey={sortField}
              direction={sortDirection}
              onSort={onSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="activity" currentWidthPx={getColumnWidthPx('activity')} onResizeStart={startResize} />
              )}
            </SortableTh>
            <SortableTh
              style={{ width: getColumnWidth('status') }}
              label="Status"
              sortKey="status"
              activeKey={sortField}
              direction={sortDirection}
              onSort={onSort}
            >
              {screenSize === 'desktop' && (
                <ResizeHandle columnId="status" currentWidthPx={getColumnWidthPx('status')} onResizeStart={startResize} />
              )}
            </SortableTh>
            {/* Pinned right and sized to its controls (expander + kebab), so it stays on screen
                while the middle columns scroll and never overlaps Status (W-M8). Not resizable. */}
            <th className="wrap-pin-right" style={{ width: ACTIONS_COL_WIDTH_ROUTINE }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {displayTasks.map((task) => {
            const isExpanded = expandedRows.includes(task.id);
            const hasEfforts = task.efforts?.length ?? 0 > 0; 

            // Get the first activity name and number using explicit null checking
            const activityDisplay = (() => {
              try {
                if (Array.isArray(task.activities) && task.activities.length > 0) {
                  const firstActivity = task.activities[0];
                  if (firstActivity && typeof firstActivity.activityName === 'string') {
                    const activityNumber = firstActivity.activityNumber || '';
                    const activityName = firstActivity.activityName || '';
                    return activityNumber ? `#${activityNumber} - ${activityName}` : activityName;
                  }
                  return 'No Activity Name';
                }
                return 'No Activity';
              } catch (err) {
                console.error('Error accessing activity name:', err);
                return 'No Activity';
              }
            })();
            
            // Get the first asset name with proper null checking
            const assetName = (() => {
              try {
                if (Array.isArray(task.taskLocations) && task.taskLocations.length > 0) {
                  const firstLocation = task.taskLocations[0];
                  if (firstLocation && firstLocation.asset) {
                    return typeof firstLocation.asset.name === 'string' 
                      ? firstLocation.asset.name 
                      : 'Unnamed Asset';
                  }
                }
                return 'No Asset';
              } catch (err) {
                console.error('Error accessing asset name:', err);
                return 'No Asset';
              }
            })();
            
            // Get completion quantity safely
            const quantity = (() => {
              try {
                const value = task.completionAccomplishment;
                return typeof value === 'number' ? value : 0;
              } catch (err) {
                console.error('Error accessing completion quantity:', err);
                return 0;
              }
            })();

            return (
              <React.Fragment key={task.id}>
                <tr>
                  <td className="wrap-touch-check wrap-pin-left">
                    <input
                      type="checkbox"
                      checked={selectedTasks && selectedTasks.includes(task.id)}
                      onChange={() => handleSelectTask(task.id)}
                      className="tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary"
                    />
                  </td>
                  <td
                    className="wrap-pin-left"
                    style={{ width: EXPAND_COL_WIDTH, '--wrap-pin-left': CHECK_COL_WIDTH } as React.CSSProperties}
                  >
                    {task.efforts && task.efforts.some((e) => e.quantity || e.rate) && (
                      <RowExpandToggle
                        expanded={isExpanded}
                        onToggle={() => toggleRowExpansion(task.id, task)}
                        label={isExpanded ? 'Hide efforts' : 'Show efforts'}
                      />
                    )}
                  </td>
                  <td
                    className="wrap-pin-left wrap-pin-edge"
                    style={{ '--wrap-pin-left': TASK_ID_PIN_LEFT } as React.CSSProperties}
                  >
                    <IdLink onClick={(e) => { e.preventDefault(); onTaskClick?.(task); }}>
                      {task.taskNumber || `T-${task.id}`}
                    </IdLink>
                  </td>
                  <td>
                    {formatEffortDate(getTaskWorkDate(task))}
                  </td>
                  <td>
                    {/* CSS clamp at the real cell edge, not a fixed character count (W-M7). */}
                    {task.name ? <RoutineTaskName name={task.name} /> : <EmptyValue />}
                  </td>
                  <td>
                    {task.description ? <ClampText>{task.description}</ClampText> : <EmptyValue />}
                  </td>
                  {showAsset && (
                    <td>
                      {assetName ? <ClampText>{assetName}</ClampText> : <EmptyValue />}
                    </td>
                  )}
                  {showQuantity && (
                    <td>{quantity.toFixed(2)}</td>
                  )}
                  <td>
                    {activityDisplay ? <ClampText>{activityDisplay}</ClampText> : <EmptyValue />}
                  </td>
                  <td>
                    <StatusBadge status={task.status} />
                  </td>
                  <td className="wrap-pin-right">
                    {/* V2-072: only the kebab here; the expander leads the row. */}
                    <RoutineTaskActions
                      task={task}
                      onAction={(action) => onTaskAction(action, task)}
                      canReopenTask={canReopenTask}
                      canCloneTask={canCloneTask}
                    />
                  </td>
                </tr>
                {isExpanded && screenSize !== 'tablet' && renderEffortDetails(task)}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      </TableScroll>

      {/* Add modal for tablet effort details */}
      {selectedTaskForModal && (
        <EffortModal
          show={!!selectedTaskForModal}
          onHide={() => setSelectedTaskForModal(null)}
          task={selectedTaskForModal}
          canToggleVehicleFuel={canToggleVehicleFuel}
          onVehicleFuelEffortPatched={handleVehicleFuelEffortPatched}
        />
      )}
    </div>
  );
};

export default RoutineWorkTable; 